import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Building2, Users, FileSignature, Receipt, Wrench,
  Bell, BarChart3, MessageSquare, ScrollText,
  Home, Banknote, Calendar, UserCog, IndianRupee, KeyRound, Shield, Palette,
  Briefcase, ShieldCheck, User as UserIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBranding } from '@/lib/branding';
import { CapinLogo } from '@/components/brand/CapinLogo';

const NAVS = {
  owner: [
    { to: '/owner', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/owner/properties', label: 'Properties', icon: Building2 },
    { to: '/owner/lease-expiry', label: 'Lease Expiry', icon: Calendar },
    { to: '/owner/maintenance', label: 'Maintenance Review', icon: Wrench },
    { to: '/owner/financial', label: 'Financial Summary', icon: IndianRupee },
    { to: '/owner/reports', label: 'Reports', icon: BarChart3 },
    { to: '/profile', label: 'My Profile', icon: UserIcon },
  ],
  consultant: [
    { to: '/consultant', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/consultant/properties', label: 'Properties & Units', icon: Building2 },
    { to: '/consultant/tenants', label: 'Tenants', icon: Users },
    { to: '/consultant/leases', label: 'Leases', icon: FileSignature },
    { to: '/consultant/collections', label: 'Collections', icon: Receipt },
    { to: '/consultant/expenses', label: 'Expenses', icon: Banknote },
    { to: '/consultant/maintenance', label: 'Maintenance', icon: Wrench },
    { to: '/consultant/notices', label: 'Notices', icon: Bell },
    { to: '/consultant/reports', label: 'Reports', icon: BarChart3 },
    { to: '/profile', label: 'My Profile', icon: UserIcon },
  ],
  tenant: [
    { to: '/tenant', label: 'Dashboard', icon: Home, end: true },
    { to: '/tenant/lease', label: 'My Lease', icon: FileSignature },
    { to: '/tenant/payments', label: 'Rent & Payments', icon: Receipt },
    { to: '/tenant/maintenance', label: 'Maintenance', icon: Wrench },
    { to: '/tenant/notices', label: 'Notices', icon: Bell },
    { to: '/tenant/messages', label: 'Messages', icon: MessageSquare },
    { to: '/profile', label: 'My Profile', icon: UserIcon },
  ],
  agent: [
    { to: '/agent', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/agent/properties', label: 'My Properties', icon: Building2 },
    { to: '/agent/listings', label: 'Available Listings', icon: Home },
    { to: '/agent/notes', label: 'My Notes', icon: MessageSquare },
    { to: '/profile', label: 'My Profile', icon: UserIcon },
  ],
  admin: [
    { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/admin/users', label: 'Users', icon: UserCog },
    { to: '/admin/owners', label: 'Owners', icon: KeyRound },
    { to: '/admin/consultants', label: 'Consultants', icon: Shield },
    { to: '/admin/agents', label: 'Agents', icon: Briefcase },
    { to: '/admin/tenants', label: 'Tenants', icon: Users },
    { to: '/admin/properties', label: 'Properties', icon: Building2 },
    { to: '/admin/leases', label: 'Leases', icon: FileSignature },
    { to: '/admin/collections', label: 'Rent & Payments', icon: Receipt },
    { to: '/admin/assignments', label: 'Assignments', icon: Users },
    { to: '/admin/kyc', label: 'KYC Approvals', icon: ShieldCheck },
    { to: '/admin/branding', label: 'Branding', icon: Palette },
    { to: '/admin/audit-log', label: 'Audit Log', icon: ScrollText },
    { to: '/profile', label: 'My Profile', icon: UserIcon },
  ],
};

const ROLE_PORTAL_LABEL = {
  admin: 'Super Admin portal',
  owner: 'Owner portal',
  consultant: 'Consultant portal',
  agent: 'Agent portal',
  tenant: 'Tenant portal',
};

export function SidebarContent({ role, branding, onNavigate }) {
  const items = NAVS[role] || [];
  return (
    <>
      <div className="px-5 py-5 flex items-center gap-2 shrink-0">
        <CapinLogo size={38} monogram={branding.logo_monogram} imageUrl={branding.logo_url} />
        <div className="min-w-0">
          <div className="text-sm font-semibold leading-tight truncate">
            {branding.brand_name}
            <span style={{ color: '#d4a017' }}> {branding.brand_suffix}</span>
          </div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{ROLE_PORTAL_LABEL[role] || role}</div>
        </div>
      </div>
      <nav className="px-3 pb-6 space-y-1 overflow-y-auto flex-1" aria-label="Primary">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            onClick={onNavigate}
            data-testid={`sidebar-nav-item-${it.to.replace(/\//g, '-')}`}
            className={({ isActive }) => cn(
              'group flex items-center gap-3 rounded-lg px-3 py-2.5 min-h-[44px] text-sm font-medium transition-[color,background-color] duration-150 relative',
              isActive
                ? 'bg-muted/80 text-foreground before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-full before:bg-[hsl(var(--role-accent))]'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
            )}
          >
            <it.icon className="h-4 w-4 shrink-0" />
            <span className="truncate">{it.label}</span>
          </NavLink>
        ))}
      </nav>
    </>
  );
}

export function Sidebar({ role }) {
  const { branding } = useBranding();
  return (
    <aside
      data-testid="app-sidebar"
      className="hidden md:flex md:flex-col w-[252px] shrink-0 border-r bg-[hsl(var(--sidebar))] text-[hsl(var(--sidebar-foreground))] sticky top-0 h-dvh"
    >
      <SidebarContent role={role} branding={branding} />
    </aside>
  );
}

export function MobileBottomNav({ role }) {
  const items = (NAVS[role] || []).slice(0, 5);
  return (
    <nav
      data-testid="tenant-bottom-nav"
      className="fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur md:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label="Tenant quick navigation"
    >
      <div className="grid grid-cols-5">
        {items.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end}
                   data-testid={`bottom-nav-item-${it.to.replace(/\//g, '-')}`}
                   className={({ isActive }) => cn(
                     'flex flex-col items-center justify-center gap-0.5 py-2 min-h-[52px] text-[11px] transition-colors',
                     isActive ? 'text-[hsl(var(--role-accent))]' : 'text-muted-foreground'
                   )}>
            <it.icon className="h-4 w-4" />
            <span className="truncate max-w-[64px]">{it.label.split(' ')[0]}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
